import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Video, PlayCircle, Film, Loader2, Radio, Users, MoreVertical, Archive, ArchiveRestore, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const HEADER_IMG = "https://images.unsplash.com/photo-1537882111161-c3379a777c8b";

export default function Dashboard() {
  const { user, team } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [opponent, setOpponent] = useState("");
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const isRecorder = user?.role === "recorder";
  const isSuper = !!user?.is_super_admin;

  const archiveGame = async (g) => {
    try { await api.post(`/games/${g.id}/archive`); toast.success(`"${g.name}" archived — hidden from other coaches`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unarchiveGame = async (g) => {
    try { await api.post(`/games/${g.id}/unarchive`); toast.success(`"${g.name}" restored`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    const g = pendingDelete;
    setPendingDelete(null);
    if (!g) return;
    try { await api.delete(`/games/${g.id}`); toast.success(`"${g.name}" deleted`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/games");
      setGames(data);
    } catch {
      setGames([]);
    }
  };
  useEffect(() => { load(); }, []);

  const createGame = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/games", { name, opponent });
      setOpen(false);
      setName(""); setOpponent("");
      toast.success("Game created");
      navigate(isRecorder ? `/record/${data.id}` : `/game/${data.id}`);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <div className="relative h-52 sm:h-64 overflow-hidden border-b border-border">
        <img src={HEADER_IMG} alt="team" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-secondary">{team?.name || "Your Team"}</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-none mt-1">
            {isRecorder ? "Recorder Station" : "Film Room"}
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            {isRecorder
              ? "Pick a game to start filming. Every clip uploads the instant you hit stop."
              : "Review offense, defense, or the full game — clip by clip, straight from the sideline."}
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Film className="h-6 w-6 text-primary" strokeWidth={2.5} />
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Games</h2>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-game-button"
                className="h-11 bg-primary hover:bg-red-500 text-white font-heading font-bold uppercase tracking-widest transition-colors">
                <Plus className="h-5 w-5 mr-1.5" strokeWidth={3} /> New Game
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-tight">Create a game</DialogTitle>
              </DialogHeader>
              <form onSubmit={createGame} className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Game name</Label>
                  <Input data-testid="game-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Week 5 vs Riverside" className="h-11 bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Opponent (optional)</Label>
                  <Input data-testid="game-opponent-input" value={opponent} onChange={(e) => setOpponent(e.target.value)}
                    placeholder="Riverside High" className="h-11 bg-background border-border" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="create-game-submit" disabled={creating}
                    className="bg-primary hover:bg-red-500 text-white font-heading font-bold uppercase tracking-widest">
                    {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create & continue"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {games === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : games.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-md">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-2xl font-bold uppercase tracking-tight">No games yet</h3>
            <p className="text-muted-foreground mt-2">Create your first game to start capturing film.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {games.map((g, i) => (
              <div key={g.id} data-testid={`game-card-${g.id}`}
                className={`fade-up bg-card border rounded-md overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition-colors ${g.is_archived ? "border-yellow-500/40 opacity-80" : "border-border hover:border-primary/60"}`}
                style={{ animationDelay: `${i * 40}ms` }}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {g.status === "live" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-primary">
                          <Radio className="h-3.5 w-3.5 rec-dot" strokeWidth={3} /> Live
                        </span>
                      ) : (
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Final</span>
                      )}
                      {g.is_archived && (
                        <span data-testid={`archived-badge-${g.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 text-[10px] font-black uppercase tracking-widest">
                          <Archive className="h-3 w-3" strokeWidth={2.75} /> Archived
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-3xl font-black tabular-nums text-white leading-none">
                        {g.clip_count}<span className="text-sm text-muted-foreground ml-1">clips</span>
                      </span>
                      {isSuper && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`game-menu-${g.id}`} className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-white hover:bg-accent transition-colors">
                              <MoreVertical className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border">
                            {g.is_archived ? (
                              <DropdownMenuItem data-testid={`unarchive-game-${g.id}`} onClick={() => unarchiveGame(g)} className="cursor-pointer">
                                <ArchiveRestore className="h-4 w-4 mr-2" strokeWidth={2.5} /> Restore game
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem data-testid={`archive-game-${g.id}`} onClick={() => archiveGame(g)} className="cursor-pointer">
                                <Archive className="h-4 w-4 mr-2" strokeWidth={2.5} /> Archive (hide from coaches)
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem data-testid={`delete-game-${g.id}`} onClick={() => setPendingDelete(g)} className="cursor-pointer text-primary focus:text-primary">
                              <Trash2 className="h-4 w-4 mr-2" strokeWidth={2.5} /> Delete game
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  <h3 className="font-heading text-2xl font-bold uppercase tracking-tight leading-none">{g.name}</h3>
                  {g.opponent && <p className="text-sm text-muted-foreground mt-1">vs {g.opponent}</p>}
                  <div className="flex gap-2 mt-5">
                    <Button onClick={() => navigate(`/game/${g.id}`)} data-testid={`watch-game-${g.id}`}
                      variant="outline" className="flex-1 h-11 border-border bg-background hover:bg-accent font-heading font-bold uppercase tracking-wide">
                      <PlayCircle className="h-4 w-4 mr-1.5" strokeWidth={2.5} /> Watch
                    </Button>
                    <Button onClick={() => navigate(`/record/${g.id}`)} data-testid={`record-game-${g.id}`}
                      className="flex-1 h-11 bg-primary hover:bg-red-500 text-white font-heading font-bold uppercase tracking-wide transition-colors">
                      <Video className="h-4 w-4 mr-1.5" strokeWidth={2.5} /> Record
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
              <ShieldAlert className="h-6 w-6 text-primary" strokeWidth={2.5} /> Delete game?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              "{pendingDelete?.name}" and all its clips will be removed from the portal for everyone. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-game" className="bg-background border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-game" onClick={confirmDelete}
              className="bg-primary hover:bg-red-500 text-white font-heading font-bold uppercase tracking-wide">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
